'use client';

import Box from '@mui/material/Box';
import { alpha, type SxProps, type Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { GlassTooltip } from '../GlassTooltip';

export type DecisionOutcome = 'APPROVED' | 'DENIED' | 'WITHDRAWN';

export interface DecisionHistoryEntry {
  id: string;
  outcome: DecisionOutcome;
  /** Days between queue-entry and decision. */
  daysToDecide?: number;
  /** ISO datetime — shown in cell tooltip. */
  decidedAt?: string;
  /** Optional human label for the cell tooltip (e.g. case ID, case name). */
  label?: ReactNode;
}

export interface DecisionHistoryStripProps {
  /** Entries in chronological order (oldest left, newest right). */
  entries: DecisionHistoryEntry[];
  /** Strip height in pixels. Default 24. */
  cellHeight?: number;
  /** Cell width in pixels. Default 8. */
  cellWidth?: number;
  /** Gap between cells in pixels. Default 2. */
  cellGap?: number;
  /** Optional header label (e.g. "Decision history · last 90 days"). */
  title?: ReactNode;
  /**
   * Show the summary stats row below the strip. Default true. Stats are derived from
   * the entries (count by outcome + avg days-to-decide + commitment rate).
   */
  showStats?: boolean;
  /** Optional custom stats row — replaces the auto-generated stats when provided. */
  statsSlot?: ReactNode;
  /** Sx overrides on the outer Box. */
  sx?: SxProps<Theme>;
}

interface OutcomeStyle {
  color: string;
  label: string;
}

function styleFor(theme: Theme, outcome: DecisionOutcome): OutcomeStyle {
  switch (outcome) {
    case 'APPROVED':
      return { color: theme.palette.success.main, label: 'Approved' };
    case 'DENIED':
      return { color: theme.palette.error.main, label: 'Denied' };
    case 'WITHDRAWN':
      return { color: theme.palette.text.disabled, label: 'Withdrawn' };
  }
}

function pluralize(count: number, singular: string, plural?: string) {
  return `${count} ${count === 1 ? singular : (plural ?? `${singular}s`)}`;
}

export function DecisionHistoryStrip({
  entries,
  cellHeight = 24,
  cellWidth = 8,
  cellGap = 2,
  title,
  showStats = true,
  statsSlot,
  sx,
}: DecisionHistoryStripProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const approved = entries.filter((e) => e.outcome === 'APPROVED').length;
  const denied = entries.filter((e) => e.outcome === 'DENIED').length;
  const withdrawn = entries.filter((e) => e.outcome === 'WITHDRAWN').length;

  const decided = entries.filter((e) => e.outcome !== 'WITHDRAWN');
  const decidedWithDays = decided
    .map((e) => e.daysToDecide)
    .filter((d): d is number => typeof d === 'number' && Number.isFinite(d));
  const avgDays =
    decidedWithDays.length > 0 ? decidedWithDays.reduce((sum, d) => sum + d, 0) / decidedWithDays.length : null;
  const committedRate = decided.length > 0 ? Math.round((approved / decided.length) * 100) : null;

  return (
    <Box sx={sx}>
      {title && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: 'text.secondary',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            mb: 0.75,
          }}
        >
          {title}
        </Typography>
      )}
      <Box
        role="img"
        aria-label={`Decision history: ${pluralize(approved, 'approved')}, ${pluralize(
          denied,
          'denied',
        )}, ${pluralize(withdrawn, 'withdrawn')}`}
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: `${cellGap}px`,
          py: 0.5,
        }}
      >
        {entries.map((entry) => {
          const { color, label } = styleFor(theme, entry.outcome);
          const tooltipBody = (
            <Box sx={{ minWidth: 140 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {label}
                {entry.label && ' · '}
                {entry.label}
              </Typography>
              {entry.daysToDecide != null && (
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                  {entry.daysToDecide.toFixed(1)} days to decide
                </Typography>
              )}
              {entry.decidedAt && (
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                  {new Date(entry.decidedAt).toLocaleDateString()}
                </Typography>
              )}
            </Box>
          );
          return (
            <GlassTooltip key={entry.id} title={tooltipBody} placement="top">
              <Box
                sx={{
                  width: cellWidth,
                  height: cellHeight,
                  borderRadius: 0.5,
                  backgroundColor: alpha(color, isDark ? 0.85 : 1),
                  flexShrink: 0,
                }}
              />
            </GlassTooltip>
          );
        })}
      </Box>

      {showStats && (
        <Box
          sx={{
            mt: 1,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: { xs: 1, sm: 2 },
          }}
        >
          {statsSlot ?? (
            <>
              <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
                {pluralize(approved, 'approved')}
              </Typography>
              <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 600 }}>
                {pluralize(denied, 'denied')}
              </Typography>
              {avgDays != null && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {avgDays.toFixed(1)} days avg time to decide
                </Typography>
              )}
              {committedRate != null && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {committedRate}% commitment rate
                </Typography>
              )}
            </>
          )}
        </Box>
      )}
    </Box>
  );
}

DecisionHistoryStrip.displayName = 'DecisionHistoryStrip';

export default DecisionHistoryStrip;
